/**
 * PageHeader — Cinematic page header with gradient band, decorative orbs,
 * stat strip, breadcrumbs, and action slot.
 *
 * Design: DESIGN_FUSION_MAP.md + DESIGN-SYSTEM-UPGRADE-SPEC.md
 * - heroGradientElevated() background with CSS-variable-backed colors
 * - Floating decorative orbs (kPulseGlow animation)
 * - Title: 28px/800 weight, -0.02em tracking, kReveal animation
 * - Subtitle: 14px secondary, staggered reveal
 * - StatPill strip with hover lift
 * - Actions slot with fade-in
 * - Fully theme-switchable (dark/light)
 */

import React, { type CSSProperties, type ReactNode } from 'react';
import { css, flexRow, heroGradientElevated, orbStyleElevated, EASE_OUT_EXPO } from '@kantera/ui';

// ---------------------------------------------------------------------------
// StatPill — compact stat card used in the header strip
// ---------------------------------------------------------------------------
export interface StatPillProps {
  label: string;
  value: string | number;
  accent?: boolean;
  icon?: ReactNode;
  trend?: 'up' | 'down' | 'neutral';
}

export const StatPill: React.FC<StatPillProps> = ({ label, value, accent, icon, trend }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '2px',
      padding: '12px 20px',
      background: 'var(--k-bg-card)',
      border: '1px solid var(--k-border)',
      borderRadius: '12px',
      minWidth: '120px',
      transition: 'border-color 150ms ease, transform 150ms ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.border = '1px solid var(--k-border-hover)';
      e.currentTarget.style.transform = 'translateY(-1px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.border = '1px solid var(--k-border)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  >
    {icon && (
      <div
        style={{ marginBottom: '2px', color: accent ? 'var(--k-accent)' : 'var(--k-text-muted)' }}
      >
        {icon}
      </div>
    )}
    <span
      style={{
        fontSize: '24px',
        fontWeight: 700,
        color: accent ? 'var(--k-accent)' : 'var(--k-text-primary)',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.01em',
        lineHeight: 1.1,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      {value}
      {trend && trend !== 'neutral' && (
        <span
          style={{
            fontSize: '12px',
            color: trend === 'up' ? 'var(--k-success)' : 'var(--k-danger)',
          }}
        >
          {trend === 'up' ? '↑' : '↓'}
        </span>
      )}
    </span>
    <span
      style={{
        fontSize: '11px',
        fontWeight: 500,
        color: 'var(--k-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// PageHeader
// ---------------------------------------------------------------------------
export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  stats?: StatPillProps[];
  breadcrumb?: ReactNode;
  /** CSS variable for page-specific accent tint (default: var(--k-accent)) */
  accentColor?: string;
  /** Show cinematic gradient background with orbs (default: true) */
  cinematic?: boolean;
  style?: CSSProperties;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  stats,
  breadcrumb,
  accentColor = 'var(--k-accent)',
  cinematic = true,
  style: customStyle,
}) => {
  const wrapperStyle = cinematic
    ? css(
        heroGradientElevated(accentColor),
        {
          padding: '32px 24px 24px',
          marginBottom: '24px',
        },
        customStyle,
      )
    : css(
        {
          marginBottom: '24px',
          animation: `kFadeIn 300ms ${EASE_OUT_EXPO} both`,
        },
        customStyle,
      );

  return (
    <div style={wrapperStyle}>
      {/* Decorative orbs (cinematic mode only) */}
      {cinematic && (
        <>
          <div style={orbStyleElevated(200, `${accentColor}`, '70%', '-20%')} />
          <div style={orbStyleElevated(150, 'var(--k-brand-glow)', '-5%', '50%')} />
        </>
      )}

      {breadcrumb && (
        <div style={{ marginBottom: '8px', position: 'relative', zIndex: 1 }}>{breadcrumb}</div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 800,
              color: 'var(--k-text-primary)',
              margin: 0,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              animation: `kReveal 400ms ${EASE_OUT_EXPO} both`,
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              style={{
                fontSize: '14px',
                color: 'var(--k-text-secondary)',
                margin: '8px 0 0',
                lineHeight: 1.5,
                animation: `kReveal 400ms ${EASE_OUT_EXPO} 100ms both`,
              }}
            >
              {subtitle}
            </p>
          )}
        </div>

        {actions && (
          <div
            style={css(flexRow('8px'), {
              flexShrink: 0,
              animation: `kFadeIn 300ms ${EASE_OUT_EXPO} 200ms both`,
            })}
          >
            {actions}
          </div>
        )}
      </div>

      {stats && stats.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '16px',
            flexWrap: 'wrap',
            position: 'relative',
            zIndex: 1,
            animation: `kReveal 400ms ${EASE_OUT_EXPO} 200ms both`,
          }}
        >
          {stats.map((stat, i) => (
            <StatPill key={i} {...stat} />
          ))}
        </div>
      )}
    </div>
  );
};
